'use client';

import { prescriptionRecognition } from '@/api/product';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import {
  BasePrescriptionRecognition,
  ILoadPR,
  IPrescriptionRecognition,
} from '@/app/_lens-components/types';
import { getApiPrescriptionType } from '@/app/_lens-components/utils';
import { getEmptyPrescription } from '@/components/PrescriptionFormV2/utils';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { env } from '@/utils/env';
import { InfoOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { TrackingService } from '../services/TrackingService';
import PrGuidePop from './PrGuidePop';
import ResultDialog from './ResultDialog';
import ScanItem from './ScanItem';
import ScanningDialog from './ScanningDialog';
import UploadDialog from './UploadDialog';
import ScanPrescription from './ScanPrescription';
import useResponsive from '@/hooks/useResponsive';

/** AB 测试数据，用于控制处方流程展示 */
export interface AbTestingData {
  isShowPresMethods: boolean;
  isShowNewRecognition: boolean;
}

export interface PrescriptionRecognitionRef {
  showNewRecognition: boolean;
  closeNewRecognition: () => void;
}

interface PrescriptionRecognitionProps {
  prescriptionType: EPrescriptionType;
  presTypeOptionName: string;
  prLoadAnimationTime: number;
  onLoadPrescription: (data: ILoadPR) => void;
  enablePrescriptionRecognitionNoviceGuide: boolean;
  renderChooseMine: () => React.ReactNode;
  prescriptionItemClassName: string;
  iconContainerClassName: string;
  textClassName: string;
  prImageClassName: string;
  ref: React.RefObject<PrescriptionRecognitionRef>;
  /** AB 测试数据（可选） */
  abTestingData: AbTestingData;
}

export default function PrescriptionRecognition({
  prescriptionType,
  presTypeOptionName,
  prLoadAnimationTime,
  onLoadPrescription,
  enablePrescriptionRecognitionNoviceGuide,
  renderChooseMine,
  prescriptionItemClassName,
  iconContainerClassName,
  textClassName,
  prImageClassName,
  abTestingData,
  ref,
}: PrescriptionRecognitionProps) {
  const t = useTranslations('lens-process');

  // ===== 全局状态 =====
  const { trackEvent } = useReporting();
  const { lensGoodsItem, lensGoodsSensorsData, setScanInfo, setStepBtnText } =
    useLensProcessStore();
  const { isMobile } = useResponsive();
  // 创建埋点服务
  const trackingService = new TrackingService(
    trackEvent,
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
  );

  // ===== 组件状态 =====
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  // 是否取消过处方识别。取消之后，后续流程不再处理，使用Ref永远保持最新值
  const isCancelScanRef = useRef(false);
  const [showResultPop, setShowResultPop] = useState(false);
  const [resultData, setResultData] = useState<BasePrescriptionRecognition>(null);
  const [uploadedS3Url, setUploadedS3Url] = useState<string>('');
  const [showImage, setShowImage] = useState(false);
  const [showPrGuide, setShowPrGuide] = useState(false);
  const [showNewRecognition, setShowNewRecognition] = useState(false);

  // Portal相关状态
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);

  // ===== 处方识别相关函数 =====
  const onScanClick = () => {
    if (showPrGuide) {
      handleClosePrGuide();
    }
    trackingService.prescriptionEnterPageScanEntryClick();
    if (abTestingData.isShowNewRecognition) {
      setShowNewRecognition(true);

      setStepBtnText('');
    } else {
      setShowUploadDialog(true);
    }

    // 发送弹窗曝光事件
    trackingService.prescriptionEnterPageScanPopupExp();
  };

  // 处理文件上传
  const handleFileUpload = async (fileUrl: string) => {
    try {
      // 上传文件到S3
      if (fileUrl) {
        trackingService.prescriptionEnterPageUploadSuccessResult();
        isCancelScanRef.current = false;

        // 开始扫描处方
        startScan(fileUrl);
      } else {
        toast.error(t('uploadFailed'));
      }
    } catch (error) {
      toast.error(t('uploadFailed'));
      logger.error('Failed to upload prescription image', error);
    }
  };

  // 开始扫描处方
  const startScan = async (fileUrl: string) => {
    try {
      setIsScanning(true);
      const response = await prescriptionRecognition({
        prescription_type: getApiPrescriptionType(prescriptionType),
        product_id: lensGoodsItem?.product_id,
        prescription_img: fileUrl,
      });

      if (isCancelScanRef.current) {
        return;
      }

      if (response.code !== 200) {
        toast.error(response.msg || t('uploadFailed'));
        setIsScanning(false);
      } else {
        setUploadedS3Url(fileUrl);
        setIsScanning(false);
        const { prescription_correction: { type } = {} } = response.data;
        setResultData(response.data);

        let prescription_scan_result_type = 'failure';

        if (type === 1) {
          // 处方填写页，识别成功
          prescription_scan_result_type = 'success';
          handleScanFinish(response.data, false, fileUrl);
        } else if ([3, 5].includes(type)) {
          // 识别失败/纠错
          setShowResultPop(true);
          prescription_scan_result_type = type === 5 ? 'correction' : 'failure';
          if (type === 5) {
            trackingService.prescriptionEnterPageScanPopupChangeExp();
          } else if (type === 3) {
            trackingService.prescriptionEnterPageScanPopupFailExp();
          }
        } else {
          toast.error(t('uploadFailed'));
          prescription_scan_result_type = 'failure';
        }

        trackingService.prescriptionEnterPageScanParamResult(prescription_scan_result_type);
      }
    } catch (error) {
      if (isCancelScanRef.current) {
        return;
      }

      setIsScanning(false);
      toast.error(t('uploadFailed'));
      logger.error('Failed to scan prescription', error);
    }
  };

  // 处理扫描完成
  const handleScanFinish = (
    data: IPrescriptionRecognition,
    ignoreAdd = false,
    imageUrl?: string,
  ) => {
    setShowUploadDialog(false);
    setShowResultPop(false);
    logger.log('handleScanFinish___', data);

    const { option, prescription_type: presType, record_id } = data;
    setShowImage(true);

    // 选择忽略ADD，将ADD值恢复至默认
    let finalOption = { ...option };
    if (ignoreAdd) {
      finalOption = {
        ...finalOption,
        od_add: '0.00',
        os_add: '0.00',
      };
    }

    // 处理扫描结果
    const scannedOption = { ...getEmptyPrescription(), ...finalOption };

    // 优先使用传入的 imageUrl，如果没有则使用 uploadedS3Url
    const finalImageUrl = imageUrl || uploadedS3Url;

    setScanInfo({
      recordId: record_id,
      imageUrl: finalImageUrl,
    });

    onLoadPrescription({
      prescription: scannedOption,
      // 识别处方类型为老化时，转换为老化子类型
      presType:
        presType === EPrescriptionType.READING_GLASSES
          ? EPrescriptionType.PRESCRIPTION_READING
          : presType,
      record_id,
      prescriptions_image: finalImageUrl,
    });
    trackingService.prescriptionScanSuccessPageView(presType || '');
  };

  // 取消扫描
  const handleCancelScan = () => {
    setIsScanning(false);
    isCancelScanRef.current = true;
  };

  // 处理纠错弹窗确认
  const handleCorrectionConfirm = (selectedOption: { value: string; type: string }) => {
    if (selectedOption.type === 'ignoreAdd') {
      // 忽略ADD值
      handleScanFinish(resultData, true);
      trackingService.prescriptionEnterPageScanPopupChangeNextClick();
    } else if (selectedOption.type === 'changePres') {
      // 切换处方类型
      handleScanFinish({
        ...resultData,
        prescription_type: selectedOption.value,
      });
      trackingService.prescriptionEnterPageScanPopupChangeNextClick(selectedOption.value);
    }
  };

  // 处理点击上传按钮
  const handleClickUpload = (buttonType: 'Upload' | 'Photo') => {
    trackingService.prescriptionEnterPageScanPopupClick(buttonType);
  };

  // 初始化Portal容器
  useEffect(() => {
    setMounted(true);
    const targetElement = document.getElementById('right-box-content');
    setPortalContainer(targetElement);
  }, []);

  // 处理新手引导
  useEffect(() => {
    const condition2 = localStorage.getItem('zl-has-shown-pr-guide');
    if (enablePrescriptionRecognitionNoviceGuide && !condition2) {
      setShowPrGuide(true);
    }
  }, [enablePrescriptionRecognitionNoviceGuide]);

  const handleClosePrGuide = () => {
    setShowPrGuide(false);
    localStorage.setItem('zl-has-shown-pr-guide', 'true');
  };

  // 渲染扫描按钮
  const renderScanItem = () => (
    <ScanItem
      onClick={onScanClick}
      prescriptionItemClassName={prescriptionItemClassName}
      iconContainerClassName={iconContainerClassName}
      textClassName={textClassName}
      prImageClassName={prImageClassName}
    />
  );
  const closeNewRecognition = () => {
    setShowNewRecognition(false);
    setStepBtnText(t('submit'));
  };
  useImperativeHandle(ref, () => ({
    showNewRecognition,
    closeNewRecognition,
  }));

  // 渲染新识别组件的Portal内容
  const renderNewRecognitionPortal = () => {
    if (!abTestingData.isShowNewRecognition || !showNewRecognition || !mounted) {
      return null;
    }

    const content = (
      <div
        className={`absolute top-0 bg-[#f8f8f8] ${isMobile ? 'h-full fixed inset-0 z-105 pt-[45px] overflow-y-auto' : `z-78 pr-[10px] pb-[100px]`}`}
      >
        <ScanPrescription
          prescriptionType={prescriptionType}
          presTypeOptionName={presTypeOptionName}
          prLoadAnimationTime={prLoadAnimationTime}
          onFinsihedRecognition={(fileUrl) => {
            setUploadedS3Url(fileUrl);
            setStepBtnText(t('submit'));
          }}
          onLoadPrescription={(data) => {
            setShowNewRecognition(false);
            setShowImage(true);
            onLoadPrescription(data);
          }}
        />
      </div>
    );

    // 如果找到了目标容器，使用Portal渲染
    if (portalContainer && isMobile) {
      return createPortal(content, portalContainer);
    }

    // 否则正常渲染（降级处理）
    return content;
  };

  return (
    <>
      <div className="">
        <div className="flex justify-between items-center">
          {renderChooseMine()}
          {renderScanItem()}
        </div>
        {showImage && (
          <div className="mt-[10px] flex justify-center bg-[#fff9d7] p-[12px] rounded-lg">
            <div className="">
              <InfoOutlined />
            </div>
            <div className="flex-1 ml-[9px]">
              <p className="pf12">
                {t('pleaseConfirmThatThePrescriptionMatchesTheFollowingInformation')}
              </p>
              <PhotoProvider key={uploadedS3Url} maskClassName="!bg-black/50" bannerVisible={false}>
                <PhotoView src={env.NEXT_PUBLIC_S3_URL + uploadedS3Url}>
                  <span className="pf12 mt-[12px] underline cursor-pointer inline-block">
                    {t('viewUploadedPrescription')}
                  </span>
                </PhotoView>
              </PhotoProvider>
            </div>
          </div>
        )}

        {/* 上传弹窗 */}
        <UploadDialog
          isOpen={showUploadDialog}
          onClose={() => setShowUploadDialog(false)}
          onFileUpload={handleFileUpload}
          onClickUpload={handleClickUpload}
        />

        {/* 扫描中弹窗 */}
        <ScanningDialog
          isOpen={isScanning}
          onCancel={handleCancelScan}
          animationTime={prLoadAnimationTime}
        />

        {/* 结果弹窗 */}
        <ResultDialog
          isOpen={showResultPop}
          onClose={() => setShowResultPop(false)}
          resultData={resultData}
          uploadedS3Url={uploadedS3Url}
          onCorrectionConfirm={handleCorrectionConfirm}
        />

        {/* 新手引导 */}
        <PrGuidePop
          showPrGuide={showPrGuide}
          handleClosePrGuide={handleClosePrGuide}
          scanItem={renderScanItem}
        />
      </div>
      {/* 使用Portal渲染新识别组件到right-box-content容器 */}
      {renderNewRecognitionPortal()}
    </>
  );
}
